import { useMutation, useQuery } from '@tanstack/react-query';
import type { GridApi, IServerSideGetRowsParams } from 'ag-grid-enterprise';
import type { AxiosError } from 'axios';
import { atom, useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';

import type {
  QBFilter,
  QBFilterDefinitionsResponseDTO,
  QBFilterOptions
} from '@/components/QueryBuilder/QueryBuilder.types';
import {
  mapQBFilterDefinitionsToConfig,
  mapQBGroupByOptions
} from '@/components/QueryBuilder/QueryBuilder.utils';
import type { TimePresetKeyWithField } from '@/components/TimeFilter/TimeFilter.types';
import { useHandleError } from '@/hooks/useHandleError';
import { axiosClient, queryClient } from '@/services/BaseApi.service';

import { selectedOrganizationAtom } from '../Organizations';
import {
  CASES_HIDDEN_FIELDS,
  CASES_INTERNAL_SEARCH_FIELD_LABELS,
  CASES_SEARCH_FIELD_LABELS
} from './Cases.constants';
import { mapCaseFiltersToRequestParams, mapCases } from './Cases.utils';

export const casesFiltersAtom = atom<QBFilter[] | null>(null);
export const casesTimeFilterAtom = atom<TimePresetKeyWithField | null>(null);

export const internalCasesFiltersAtom = atom<QBFilter[] | null>(null);
export const internalCasesTimeFilterAtom = atom<TimePresetKeyWithField | null>(
  null
);

export const casesGridApiAtom = atom<GridApi | null>(null);
export const internalCasesGridApiAtom = atom<GridApi | null>(null);

export const getCaseServerSideDatasource = (
  organizationLabel: string,
  caseFilters?: QBFilter[] | null,
  caseTimeFilter?: TimePresetKeyWithField,
  onLoadingChange?: (isLoading: boolean) => void
) => {
  return {
    getRows: async (params: IServerSideGetRowsParams) => {
      onLoadingChange?.(true);
      const { startRow, sortModel } = params.request;
      const pageSize = params.api.paginationGetPageSize();
      const pagesToFetch = pageSize * 5;

      // Build query parameters
      const queryParams = new URLSearchParams({
        page: (Math.floor((startRow as number) / pagesToFetch) + 1).toString(),
        page_size: pagesToFetch.toString()
      });

      const requestParams = mapCaseFiltersToRequestParams(
        caseFilters || [],
        caseTimeFilter
      );

      // Add organization filter if it is not the all organizations option
      if (organizationLabel !== 'all' && organizationLabel !== 'noop') {
        requestParams.organization__label = organizationLabel;
      }

      // Add sorting if specified
      if (sortModel && sortModel.length > 0) {
        let { colId } = sortModel[0];
        const { sort } = sortModel[0];
        const sortPrefix = sort === 'asc' ? '' : '-';
        if (colId === 'organization_name') {
          colId = 'organization__name';
        }
        requestParams.order_by = `${sortPrefix}${colId}`;
      }

      if (
        organizationLabel !== 'all' &&
        organizationLabel !== 'noop' &&
        requestParams.order_by === 'organization__name'
      ) {
        delete requestParams.order_by; // Remove primary sorting by organization name when filtering by specific organization
      }

      try {
        const response = await queryClient.fetchQuery({
          queryKey: [
            'v2/cases/search',
            Object.fromEntries(queryParams),
            requestParams
          ],
          queryFn: () =>
            axiosClient
              .post(`/v2/cases/search?${queryParams.toString()}`, requestParams)
              .then((resp) => resp.data),
          staleTime: 5 * 60 * 1000
        });

        const mappedRows = mapCases(response.data) || [];

        const result = {
          rowData: mappedRows,
          rowCount: response.pagination.total || 0
        };
        params.success(result);
      } catch (error) {
        console.error('Server-side datasource error:', error);
        params.fail();
      } finally {
        onLoadingChange?.(false);
      }
    }
  };
};

export const useFetchCaseFilters = () => {
  const handleError = useHandleError();
  const { pathname } = useLocation();

  const { data, isError, error, isLoading } = useQuery({
    queryKey: ['v2/cases/filters'],
    staleTime: Infinity,
    select: (data) =>
      mapQBFilterDefinitionsToConfig(
        data as QBFilterDefinitionsResponseDTO,
        pathname.includes('internal')
          ? CASES_INTERNAL_SEARCH_FIELD_LABELS
          : CASES_SEARCH_FIELD_LABELS,
        pathname.includes('internal') ? [] : CASES_HIDDEN_FIELDS,
        [
          { pattern: /\bAi\b/g, replacement: 'AI' },
          { pattern: /\bSoc\b/g, replacement: 'SOC' }
        ]
      )
  });

  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        'Failure to fetch case filters configuration.'
      );
    }
  }, [isError, error]);

  return { data, isLoading };
};

export const useFetchCaseGroupByOptions = (
  groups: string[],
  customFilters?: Record<
    string,
    string | string[] | number | number[] | boolean
  >,
  setValue?: (value: Record<string, QBFilterOptions[]>) => void,
  enabled = true
) => {
  const handleError = useHandleError();
  const caseFilters = useAtomValue(casesFiltersAtom);
  const caseTimeFilter = useAtomValue(casesTimeFilterAtom);
  const internalCaseFilters = useAtomValue(internalCasesFiltersAtom);
  const internalCaseTimeFilter = useAtomValue(internalCasesTimeFilterAtom);
  const selectedOrg = useAtomValue(selectedOrganizationAtom);
  const { pathname } = useLocation();
  const userIsInternal = pathname === '/cases-internal';

  const pageCaseFilters = userIsInternal ? internalCaseFilters : caseFilters;
  const pageCaseTimeFilter = userIsInternal
    ? internalCaseTimeFilter
    : caseTimeFilter;

  const scopedFilters =
    selectedOrg.id !== 0
      ? { ...customFilters, organization__label: selectedOrg.label }
      : customFilters;

  const requestParams = useMemo(() => {
    if (scopedFilters) {
      return { ...scopedFilters, groups };
    }
    const params = mapCaseFiltersToRequestParams(
      pageCaseFilters || [],
      pageCaseTimeFilter
    );
    return { ...params, groups };
  }, [pageCaseFilters, pageCaseTimeFilter, groups, scopedFilters]);

  const { data, isError, error, isLoading, isFetching, isSuccess } = useQuery({
    queryKey: ['v2/cases/group_by', requestParams],
    queryFn: () =>
      axiosClient
        .post(`/v2/cases/group_by?page=1&page_size=100`, requestParams)
        .then((resp) => resp.data),
    select: mapQBGroupByOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled:
      enabled &&
      (scopedFilters !== undefined ||
        (pageCaseFilters !== null && pageCaseTimeFilter !== null))
  });

  useEffect(() => {
    if (isSuccess && setValue) {
      setValue(data || {});
    }
  }, [data, isSuccess, setValue]);

  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        'Failure to fetch group by options for cases.'
      );
    }
  }, [isError, error, handleError]);

  return { data, isLoading, isFetching };
};

export const useFetchCaseExport = () => {
  return useMutation({
    mutationFn: ({
      format,
      caseFilters
    }: {
      format: 'json' | 'csv';
      caseFilters: Record<string, string>;
    }) => {
      const params = new URLSearchParams({
        export_format: format
      });
      return axiosClient.post('/v2/cases/export', caseFilters || {}, {
        params,
        responseType: 'blob'
      });
    }
  });
};
